/// <reference path="../nativewind-augmentation.d.ts" />
// Copyright (c) Ping. All rights reserved.
//
// Shared "Read by" popover panel. Every media tile that needs a reader list
// uses this component — it wraps AnchoredPopover and renders the avatar + name
// + time row for each reader. State (tickRef, open/close) lives in the caller.

import { View, Text } from 'react-native';
import { AnchoredPopover } from '../components/AnchoredPopover';
import { Avatar } from '../components/Avatar';

export type ReadByReader = {
  readonly userId: string;
  /** Full display name — Avatar uses this for initials (splits on spaces). */
  readonly name: string;
  readonly avatarUri?: string;
  readonly timestamp: number;
};

export interface ReadByPopoverProps {
  readonly anchorRef: { current: unknown };
  readonly open: boolean;
  readonly onClose: () => void;
  readonly readers: ReadByReader[];
  readonly placement?: 'top' | 'bottom' | 'auto';
  readonly align?: 'center' | 'start' | 'end';
}

export function ReadByPopover({
  anchorRef,
  open,
  onClose,
  readers,
  placement = 'top',
  align = 'end',
}: ReadByPopoverProps): JSX.Element | null {
  if (!open || readers.length === 0) return null;
  return (
    <AnchoredPopover
      anchorRef={anchorRef}
      open={open}
      onClose={onClose}
      placement={placement}
      align={align}
    >
      <View className="min-w-[160px] max-w-[240px] rounded-[12px] bg-white dark:bg-dark-surface-panel border border-light-border-subtle dark:border-dark-border-subtle p-3 shadow-lg">
        <Text className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-[#B5BAC3]">
          Read by
        </Text>
        {readers.map((r) => (
          <View key={r.userId} className="flex-row items-center gap-2 py-0.5">
            <Avatar
              initials={r.name}
              {...(r.avatarUri !== undefined ? { imageUri: r.avatarUri } : {})}
              size="small"
              shape="circle"
            />
            <View className="flex-1">
              <Text className="text-[13px] text-[#F5F6F8]" numberOfLines={1}>
                {r.name}
              </Text>
            </View>
            <Text className="text-[11px] text-[#B5BAC3]">
              {new Intl.DateTimeFormat(undefined, {
                hour: 'numeric',
                minute: '2-digit',
                hour12: true,
              }).format(new Date(r.timestamp))}
            </Text>
          </View>
        ))}
      </View>
    </AnchoredPopover>
  );
}
